/**
 * New-version notice for tabs left open across a deploy.
 *
 * index.html is served no-cache and every bundle is content-hashed, so a reload
 * always picks up the latest release. A tab that stays open keeps running the
 * old bundle, though. This re-reads index.html (no-store) every few minutes and
 * whenever the tab comes back into view; when its entry bundle differs from
 * the one this page loaded, it shows a persistent "new version" toast with a
 * Reload action. Production builds only.
 */
import { toast } from 'sonner'

const CHECK_EVERY_MS = 5 * 60 * 1000
const TOAST_ID = 'tesseract-new-version'

/** The hashed entry bundle an index.html points at, e.g. "/assets/index-BbYggACr.js". */
export function entryBundle(html: string): string | null {
  const m = /<script[^>]+type="module"[^>]+src="([^"]*\/assets\/index-[^"]+\.js)"/.exec(html)
    ?? /src="([^"]*\/assets\/index-[^"]+\.js)"/.exec(html)
  return m ? m[1]! : null
}

/** The entry bundle this page was loaded with. */
function loadedEntry(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')
  return el ? new URL(el.src, window.location.origin).pathname : null
}

export function installNewVersionWatch(): void {
  if (!import.meta.env.PROD) return
  const current = loadedEntry()
  if (!current) return
  let notified = false
  let busy = false

  async function check() {
    if (notified || busy || document.visibilityState !== 'visible') return
    busy = true
    try {
      const res = await fetch('/', { cache: 'no-store', headers: { accept: 'text/html' } })
      if (!res.ok) return
      const latest = entryBundle(await res.text())
      if (latest && new URL(latest, window.location.origin).pathname !== current) {
        notified = true
        toast('A new version of Tesseract is available', {
          id: TOAST_ID,
          description: 'Reload to get the latest changes. Save anything you are editing first.',
          duration: Infinity,
          action: { label: 'Reload', onClick: () => window.location.reload() },
        })
      }
    } catch {
      /* offline or mid-deploy: try again next time */
    } finally {
      busy = false
    }
  }

  window.setInterval(() => void check(), CHECK_EVERY_MS)
  document.addEventListener('visibilitychange', () => void check())
  window.addEventListener('focus', () => void check())
}
